import _ from 'lodash';
import moment from 'moment';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import VoiceMemoPlayback from '@treams/treams-app/src/components/voice-memos/VoiceMemoPlayback';
import { useLocalize, useViewer } from '@treams/treams-hooks';
import { DialogueIcon, GroupsIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Avatar,
	Callout,
	Chat,
	Container,
	DateIntervalNavigation,
	Empty,
	GraphQLErrorMessage,
	Heading3,
	ITimeMachine,
	Jumbo,
	Paragraph,
	QuillText,
	RadarChart,
	Sidebar,
	SidebarLayout,
	Skeleton,
	createTimeMachine,
	palettes,
	tokens,
} from '@treams/treams-ui';

import FeedbackResultAnswer from '../../feedback/results/FeedbackResultAnswer';
import CompetencyDisplay from '../../packages/kakapo-ui-application/common/containers/CompetencyDisplay';
import CompetencyProgressPill from '../components/CompetencyProgressPill';
import { useGrowthPathContext } from '../contexts/GrowthPathContext';
import { GrowthPathDetailContainer } from './GrowthPath';

const DateLabel = styled(Paragraph)`
	opacity: 0;
	transition: 250ms opacity ease-out;
`;

const AnswerContainer = styled(Flex)`
	page-break-inside: avoid;

	:hover {
		${DateLabel} {
			opacity: 1;
		}
	}
`;

type FeedbackScoreAnswerProps = {
	feedbackScore: graphql.CompetencyFeedbackScore;
	levels: graphql.CompetencyLevelOption[];
};

function FeedbackScoreAnswer({ feedbackScore, levels }: FeedbackScoreAnswerProps) {
	const { t } = useTranslation();

	const giver = feedbackScore.giver;
	const giverName = `${giver.firstName} ${giver.lastName}`;

	const answer = feedbackScore.feedbackResponse.answers?.find(
		(answer) => typeof answer.competencyLevelIndex === 'number',
	);

	return (
		<AnswerContainer>
			<Avatar user={giver} size={32} flexGrow={0} flexShrink={0} />
			<Box ml={2} flexGrow={1} flexShrink={1}>
				<Chat mb={3}>
					<Flex alignItems="center" justifyContent="space-between" mb={2}>
						<Heading3 mb={0}>{giverName}</Heading3>
						<DateLabel>{moment(feedbackScore.timestamp).format('LL')}</DateLabel>
					</Flex>
					{answer?.text && <QuillText value={answer.text} />}
					{typeof feedbackScore.competencyLevelIndex === 'number' && (
						<Box mt={2}>
							<CompetencyDisplay levels={levels} manager={feedbackScore.competencyLevelIndex} />
						</Box>
					)}
					{feedbackScore.competencyLevelIndex === null && (
						<Box mt={2}>
							<Paragraph fontWeight="bold">{t('feedback.answer.not.observed')}</Paragraph>
						</Box>
					)}
					{answer?.voiceMemo && (
						<Box py={2}>
							<VoiceMemoPlayback
								samples={answer.voiceMemo.samples}
								source={answer.voiceMemo.asset.source.url}
							/>
						</Box>
					)}
				</Chat>
			</Box>
		</AnswerContainer>
	);
}

function FeedbackScoreAnswerSkeleton() {
	return (
		<AnswerContainer>
			<Avatar.Skeleton size={32} flexGrow={0} flexShrink={0} />
			<Chat ml={2} flexGrow={1} flexShrink={1}>
				<Heading3.Skeleton />
				<Paragraph.Skeleton mt={2} />
			</Chat>
		</AnswerContainer>
	);
}

const SidebarContainer = styled(Box)`
	background: ${tokens.surface.secondary};
	height: 100%;

	@media print {
		display: none;
	}
`;

type CompetencyRadarChartProps = {
	feedbackScores: graphql.CompetencyFeedbackScore[];
	targetUserID: string;
	managerIDs: Set<string>;
	desiredLevelIndex?: number;
};

function CompetencyRadarChart({
	feedbackScores,
	targetUserID,
	managerIDs,
	desiredLevelIndex,
}: CompetencyRadarChartProps) {
	const { t } = useTranslation();

	const { self, managers, colleagues, external } = feedbackScores.reduce(
		(result, score) => {
			if (typeof score.competencyLevelIndex !== 'number') {
				return result;
			}

			const giverID = 'id' in score.giver ? score.giver.id : null;
			let category = 'colleagues';

			if (!giverID) {
				category = 'external';
			}

			if (giverID === targetUserID) {
				category = 'self';
			}

			if (giverID && managerIDs.has(giverID)) {
				category = 'managers';
			}

			return {
				...result,
				[category]: {
					sum: result[category].sum + score.competencyLevelIndex,
					count: result[category].count + 1,
				},
			};
		},
		{
			self: { sum: 0, count: 0 },
			managers: { sum: 0, count: 0 },
			colleagues: { sum: 0, count: 0 },
			external: { sum: 0, count: 0 },
		},
	);

	const toScore = ({ sum, count }: { sum: number; count: number }) => {
		if (count === 0) {
			return 0;
		}
		return sum / count + 1;
	};

	const scores = [toScore(self), toScore(managers), toScore(colleagues), toScore(external)];

	// Expected level is the same for all categories (it's the job requirement)
	const expectedLevel = typeof desiredLevelIndex === 'number' ? desiredLevelIndex + 1 : undefined;

	const expectedScores =
		typeof expectedLevel === 'number'
			? [expectedLevel, expectedLevel, expectedLevel, expectedLevel]
			: undefined;

	return (
		<RadarChart
			labels={[
				t('competency.radar.label.self'),
				t('competency.radar.label.manager'),
				t('competency.radar.label.colleagues'),
				t('competency.radar.label.external'),
			]}
			expectedColor={palettes.vividPink['400']}
			currentColor={palettes.freshGreen['400']}
			scores={scores}
			expectedScores={expectedScores}
		/>
	);
}

type CompetencyDetailSidebarProps = {
	feedbackScores: graphql.CompetencyFeedbackScore[];
	targetUserID: string;
	managerIDs: Set<string>;
	levels: graphql.CompetencyLevelOption[];
	achievedLevelIndex?: number;
	desiredLevelIndex?: number;
	basePath: string;
	selectedGiverId: string | null;
	timeMachine: ITimeMachine;
	onTimeMachineChange: (timeMachine: ITimeMachine) => void;
};

function CompetencyDetailSidebar({
	feedbackScores,
	targetUserID,
	managerIDs,
	levels,
	achievedLevelIndex,
	desiredLevelIndex,
	basePath,
	selectedGiverId,
	timeMachine,
	onTimeMachineChange,
}: CompetencyDetailSidebarProps) {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const history = useHistory();

	const achievedLevel =
		typeof achievedLevelIndex === 'number' ? levels[Math.round(achievedLevelIndex)] : undefined;

	const desiredLevel =
		typeof desiredLevelIndex === 'number' ? levels[desiredLevelIndex] : undefined;

	const uniqueGivers = _.uniqBy(
		feedbackScores.map((score) => score.giver),
		(giver) => ('id' in giver ? giver.id : giver.email),
	);

	return (
		<SidebarContainer p={3}>
			<Box>
				<Heading3>{t('competency.radar.title')}</Heading3>
				<Paragraph>{t('competency.radar.description')}</Paragraph>
				<Box mt={2}>
					<CompetencyRadarChart
						feedbackScores={feedbackScores}
						targetUserID={targetUserID}
						managerIDs={managerIDs}
						desiredLevelIndex={desiredLevelIndex}
					/>
				</Box>
			</Box>

			<Box mt={4}>
				{achievedLevel && (
					<Flex justifyContent="space-between" alignItems="center">
						<Paragraph>
							<strong>{t('competency.sidebar.achievedLevel')}:</strong>
							<br />
							{localize(achievedLevel.level.title).text}
						</Paragraph>
						{desiredLevelIndex !== undefined && achievedLevelIndex !== undefined && (
							<Box ml={2}>
								<CompetencyProgressPill
									progress={
										Math.round(achievedLevelIndex) > desiredLevelIndex
											? 'above-level'
											: Math.round(achievedLevelIndex) === desiredLevelIndex
												? 'on-track'
												: 'below-level'
									}
								/>
							</Box>
						)}
					</Flex>
				)}
				{desiredLevel && (
					<Paragraph mt={2}>
						<strong>{t('competency.sidebar.desiredLevel')}:</strong>
						<br />
						{localize(desiredLevel.level.title).text}
					</Paragraph>
				)}
				<Paragraph mt={2}>{t('competency.sidebar.dateFilter.description')}</Paragraph>
			</Box>

			<Box mt={4}>
				<Heading3>{t('competency.sidebar.dateFilter.title')}</Heading3>
				<Box mt={2}>
					<DateIntervalNavigation value={timeMachine} onChange={onTimeMachineChange} disableQuery />
				</Box>
			</Box>

			<Box mt={4}>
				<Heading3>{t('competency.sidebar.givers.title')}</Heading3>
				<Paragraph>{t('competency.sidebar.givers.description')}</Paragraph>
				<Box mt={2}>
					<Sidebar.Item
						small
						icon={<GroupsIcon />}
						title={t('competency.sidebar.givers.all')}
						selected={selectedGiverId === null}
						onClick={() => {
							history.push(basePath);
						}}
					/>
				</Box>
				{uniqueGivers.map((giver) => {
					const giverId = 'id' in giver ? giver.id : giver.email;

					return (
						<Box key={giverId} mt={2}>
							<Sidebar.Item
								small
								title={`${giver.firstName} ${giver.lastName}`}
								subtitle={giver.email || ''}
								icon={<Avatar user={giver} size={32} flexGrow={0} flexShrink={0} />}
								path={`${basePath}?giver=${giverId}`}
								selected={selectedGiverId === giverId}
							/>
						</Box>
					);
				})}
			</Box>
		</SidebarContainer>
	);
}

function CompetencyDetailSidebarSkeleton() {
	const { t } = useTranslation();

	return (
		<SidebarContainer p={3}>
			<Box>
				<Heading3>{t('competency.radar.title')}</Heading3>
				<Paragraph>{t('competency.radar.description')}</Paragraph>
				<Box mt={2}>
					<RadarChart.Skeleton />
				</Box>
			</Box>
		</SidebarContainer>
	);
}

const GrowthPathCompetencyDetail: FC = () => {
	const { t } = useTranslation();
	const location = useLocation();
	const viewer = useViewer();
	const { localize } = useLocalize();
	const { userID } = useGrowthPathContext();
	const { competencyID } = useParams<{ competencyID: string }>();

	// Default to 1 year interval
	const [timeMachine, setTimeMachine] = useState<ITimeMachine>(() =>
		createTimeMachine('year', moment().toDate()),
	);

	const targetUserID = userID ?? viewer?.id ?? '';

	const backUrl = location.pathname.replace(/\/competency\/[^/]+$/, '');

	const queryParams = new URLSearchParams(location.search);
	const selectedGiverId = queryParams.get('giver');

	// The server filters appraisals to `targetUserID` and scores to the time-machine
	// window, so a change of either refetches instead of re-filtering an all-users,
	// all-time result client-side.
	const {
		data: currentData,
		previousData,
		loading: queryLoading,
		error,
	} = graphql.useCompetencyFeedbackScoresQuery({
		variables: {
			competencyID,
			userID: targetUserID,
			startDate: timeMachine.current[0],
			endDate: timeMachine.current[1],
		},
		skip: !competencyID || !targetUserID,
		fetchPolicy: 'cache-and-network',
		// `feedbackScores` is nullable and its read can time out on the server; keep
		// the competency, levels and appraisal that did come back instead of failing
		// the whole page on that one field.
		errorPolicy: 'all',
	});

	// Keep the previous window on screen while the next one loads, so moving the time
	// machine does not flash the skeleton. A failed request must not fall back to it:
	// the previous window's scores would be filtered against the new window below and
	// read as an empty period, hiding the error.
	const data = currentData ?? (error ? undefined : previousData);
	const loading = queryLoading && !data;

	const { data: userCompetenciesData } = graphql.useUserCompetenciesQuery({
		variables: { id: targetUserID },
		fetchPolicy: 'cache-and-network',
		skip: !targetUserID,
	});

	const competency = data?.competency;
	const appraisals = competency?.appraisals ?? [];

	// Filter appraisals for the target user
	const userAppraisals = appraisals.filter((appraisal) => appraisal.user.id === targetUserID);
	const allFeedbackScores = userAppraisals.flatMap((appraisal) => appraisal.feedbackScores ?? []);
	// A null list is a field error on the server (the read ran out of time), not "no
	// scores": the page still renders, with a notice in place of the missing list.
	const feedbackScoresUnavailable = userAppraisals.some(
		(appraisal) => appraisal.feedbackScores === null,
	);

	// Filter by date range
	const dateFilteredScores = allFeedbackScores.filter((score) => {
		const scoreDate = moment(score.timestamp);
		return scoreDate.isBetween(timeMachine.current[0], timeMachine.current[1], undefined, '[]');
	});

	// Filter by selected giver if any
	const feedbackScores = selectedGiverId
		? dateFilteredScores.filter((score) => {
				const giverId = 'id' in score.giver ? score.giver.id : score.giver.email;
				return giverId === selectedGiverId;
			})
		: dateFilteredScores;

	const managerIDs = new Set(
		userAppraisals.flatMap(
			(appraisal) => appraisal.user.managers?.map((manager) => manager.source.id) ?? [],
		),
	);

	// Get the appraisal score (first appraisal for the user)
	const userAppraisal = userAppraisals[0];
	const appraisalScore =
		userAppraisal?.competencyLevelIndex !== undefined &&
		userAppraisal?.competencyLevelIndex !== null
			? userAppraisal.competencyLevelIndex
			: undefined;

	// Get the desired level from job competencies
	const jobCompetency = userCompetenciesData?.user.jobPositions
		.flatMap((jobPosition) => jobPosition.competencies)
		.find((jobCompetency) => jobCompetency.competency.id === competencyID);
	const desiredLevelIndex = jobCompetency?.competencyLevel;

	if (loading) {
		return <GrowthPathCompetencyDetailSkeleton />;
	}

	// With `errorPolicy: 'all'` a field error arrives next to partial data; only give
	// up on the page when nothing usable came back.
	if (error && !competency) {
		return <GraphQLErrorMessage error={error.graphQLErrors[0]} />;
	}

	const sidebar = (
		<CompetencyDetailSidebar
			feedbackScores={dateFilteredScores as graphql.CompetencyFeedbackScore[]}
			targetUserID={targetUserID}
			managerIDs={managerIDs}
			levels={competency?.levels ?? []}
			achievedLevelIndex={appraisalScore}
			desiredLevelIndex={desiredLevelIndex}
			basePath={location.pathname}
			selectedGiverId={selectedGiverId}
			timeMachine={timeMachine}
			onTimeMachineChange={setTimeMachine}
		/>
	);

	return (
		<SidebarLayout sidebar={sidebar} right>
			<GrowthPathDetailContainer>
				<Jumbo
					backButtonLink={backUrl}
					title={
						competency
							? t('competency.detail.title', { title: localize(competency.title).text })
							: undefined
					}
				>
					{competency?.description && (
						<Paragraph py={2}>
							<QuillText value={localize(competency.description).text} />
						</Paragraph>
					)}
					{competency && appraisalScore !== undefined && (
						<Box mt={2}>
							<CompetencyDisplay levels={competency.levels} manager={appraisalScore} />
						</Box>
					)}
				</Jumbo>

				<Container py={4}>
					{feedbackScoresUnavailable ? (
						<Callout color="warning">{t('growthPath.competency.feedback.unavailable')}</Callout>
					) : feedbackScores.length > 0 ? (
						<>
							<Heading3 mb={3}>{t('growthPath.competency.feedback.title')}</Heading3>
							{feedbackScores.map((feedbackScore, index) => (
								<Box key={`${feedbackScore.feedbackResponse.id}-${index}`} mt={3}>
									<FeedbackScoreAnswer
										feedbackScore={feedbackScore as graphql.CompetencyFeedbackScore}
										levels={competency?.levels ?? []}
									/>
								</Box>
							))}
						</>
					) : (
						<Skeleton.Empty
							contents={
								<Empty
									icon={<DialogueIcon />}
									title={t('growthPath.competency.feedback.empty.title')}
									description={t('growthPath.competency.feedback.empty.description')}
								/>
							}
						>
							<Skeleton.Repeat count={2}>
								<FeedbackResultAnswer.Skeleton />
							</Skeleton.Repeat>
						</Skeleton.Empty>
					)}
				</Container>
			</GrowthPathDetailContainer>
		</SidebarLayout>
	);
};

const GrowthPathCompetencyDetailSkeleton = () => {
	return (
		<SidebarLayout sidebar={<CompetencyDetailSidebarSkeleton />} right>
			<GrowthPathDetailContainer>
				<Jumbo.Skeleton />
				<Container py={4}>
					<Heading3.Skeleton mb={3} />
					<Skeleton.Repeat count={3}>
						<Box mt={3}>
							<FeedbackScoreAnswerSkeleton />
						</Box>
					</Skeleton.Repeat>
				</Container>
			</GrowthPathDetailContainer>
		</SidebarLayout>
	);
};

export default GrowthPathCompetencyDetail;
